import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  NotificationDtoJson,
  NotificationDtoJsonTypeEnum,
} from '@shared/models/api/models/NotificationDtoJson';
import { animate, style, transition, trigger } from '@angular/animations';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '@core/services/notification.service';
interface ToastNotification extends NotificationDtoJson {
  id: string;
}
@Component({
  selector: 'app-notification-toast',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification-toast.component.html',
  styleUrl: './notification-toast.component.scss',
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(400px)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateX(400px)', opacity: 0 })),
      ]),
    ]),
  ],
})
export class NotificationToastComponent implements OnInit, OnDestroy {
  private readonly notificationService = inject(NotificationService);
  toasts: ToastNotification[] = [];
  private destroy$: Subject<void> = new Subject<void>();
  private toastDuration = 5000; // Durée d'affichage de la notification en ms

  ngOnInit(): void {
    // Ecoute les nouvelles notifications
    this.notificationService.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => {
        if (notification) {
          this.showToast(notification);
        }
      });
  }

  /**
   * Affiche une notification toast
   * @param notification La notification à afficher
   */
  showToast(notification: NotificationDtoJson): void {
    const toast: ToastNotification = {
      ...notification,
      id: this.generateId(),
    };
    this.toasts.push(toast);

    // Supprime la notification après un certain temps
    setTimeout(() => this.removeToast(toast.id), this.toastDuration);
  }

  /**
   * Supprime une notification toast
   * @param id L'ID de la notification à supprimer
   */
  removeToast(id: string): void {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }

  /**
   * Gère la fin de l'animation d'une notification
   * @param toast La notification dont l'animation est terminée
   */
  onAnimationDone(toast: ToastNotification): void {
    // Optionnel: faire quelque chose après l'animation
  }

  /**
   * Génère un ID unique pour une notification toast
   * @private @returns L'ID unique généré
   */
  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  getIcon(type: NotificationDtoJsonTypeEnum): string {
    const icons: Record<NotificationDtoJsonTypeEnum, string> = {
      [NotificationDtoJsonTypeEnum.ASSIGNMENT_ACCEPTED]: '✅',
      [NotificationDtoJsonTypeEnum.MISSION_ASSIGNED_TO_DRIVER]: '🚚',
      [NotificationDtoJsonTypeEnum.ASSIGNMENT_REJECTED]: '❌',
      [NotificationDtoJsonTypeEnum.ASSIGNMENT_EXPIRED]: '⏰',
      [NotificationDtoJsonTypeEnum.MISSION_COMPLETED]: '✓',
      [NotificationDtoJsonTypeEnum.MISSION_CANCELLED]: '🚫',
      [NotificationDtoJsonTypeEnum.MISSION_DELAYED]: '⚠️',
      [NotificationDtoJsonTypeEnum.HIGH_PRIORITY_MISSION]: '🔥',
      [NotificationDtoJsonTypeEnum.DRIVER_AVAILABLE]: '👤',
      [NotificationDtoJsonTypeEnum.DRIVER_UNAVAILABLE]: '👤',
      [NotificationDtoJsonTypeEnum.SYSTEM]: '⚙️',
    };

    return icons[type] || '📬';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
